// Chinese translations. Keys mirror src/i18n/en.ts.

export const zh: Record<string, string> = {
  'app.title': '线性代数 · 可视',
  'app.sub': 'The Art of Linear Algebra',
  'nav.home': '首页',
  'nav.chapters': '章节',
  'nav.about': '关于',
  'theme.light': '浅色',
  'theme.dark': '深色',
  lang: '中',

  ch: '第',
  prev: '上一步',
  next: '下一步',
  'fig.geometry': '几何视图',
  'panel.numbers': '数值',
  'panel.controls': '控制',
  'panel.matrix': '矩阵',
  'panel.legend': '图例',

  // Home
  'home.eyebrow': '基于 Kenji Hiranabe 《The Art of Linear Algebra》',
  'home.title': '把矩阵\n变成你能\n触摸的几何。',
  'home.lede':
    '抽象符号背后藏着一组可以拉、可以拽、可以分解的几何动作。本站把那些手绘图变成实时交互——拖一下，方块就被压扁；改一格,主轴就重新对齐。',
  'home.start': '开始 →',
  'home.allchapters': '十一章 · 全部交互',
  'home.stat.chapters': '章节',
  'home.stat.interact': '可拖向量',
  'home.stat.decomp': '矩阵分解',
  'home.stat.dim': '维度优先',

  // Chapter list
  'ch01.title': '矩阵的四种视角',
  'ch01.sub': '同一矩阵可以是数字、列、行、或秩-1 的和。',
  'ch01.short': '4 种视角',
  'ch02.title': '向量 × 向量',
  'ch02.sub': '点积、外积、投影 — 拖动向量看几何意义。',
  'ch02.short': '点积 / 外积',
  'ch03.title': '矩阵 × 向量',
  'ch03.sub': 'Ax 是 A 的列的线性组合,在列空间里走。',
  'ch03.short': '线性组合',
  'ch04.title': '四个子空间',
  'ch04.sub': 'C(A) 与 N(A) 在输出, C(Aᵀ) 与 N(Aᵀ) 在输入。',
  'ch04.short': '四子空间',
  'ch05.title': '矩阵 × 矩阵',
  'ch05.sub': '点积、列、行、外积 — 同一乘法的四种解读。',
  'ch05.short': '4 种乘法',
  'ch06.title': '实用模式 P–D–E–R',
  'ch06.sub': '置换、对角、消元、旋转 — 几何上长什么样。',
  'ch06.short': 'P D E R',
  'ch07.title': 'A = CR',
  'ch07.sub': '挑选独立列,得到秩分解。',
  'ch07.short': 'CR 分解',
  'ch08.title': 'A = LU',
  'ch08.sub': '高斯消元的逐步动画。',
  'ch08.short': 'LU 分解',
  'ch09.title': 'A = QR',
  'ch09.sub': 'Gram–Schmidt 把列正交化。',
  'ch09.short': 'QR 分解',
  'ch10.title': '对称矩阵的特征分解',
  'ch10.sub': '主轴方向与拉伸量 — 从 A = QΛQᵀ 看几何。',
  'ch10.short': '特征分解',
  'ch11.title': '奇异值分解',
  'ch11.sub': '旋转 → 缩放 → 旋转,任何矩阵的三步动作。',
  'ch11.short': 'SVD',
  'ch12.title': '五分解一览',
  'ch12.sub': 'CR · LU · QR · QΛQᵀ · UΣVᵀ — 改一个 A，看五种同步刷新。',
  'ch12.short': '五分解',
  'ch13.title': '特征值地图',
  'ch13.sub': '复平面上特征值都住在哪里？十二种典型矩阵一图看清。',
  'ch13.short': 'λ 地图',
  'ch14.title': '矩阵世界',
  'ch14.sub': '所有矩阵家族的嵌套地图 — 看 A 落在哪几个圈里。',
  'ch14.short': '矩阵世界',
  'ch15.title': 'Ax = b 的解',
  'ch15.sub': '特解 + 零空间 — 四个子空间叠加上解的几何。',
  'ch15.short': 'Ax = b',
  'ch16.title': '实用模式图鉴',
  'ch16.sub': 'P1 · P2 · P1′ · P2′ · P3 · P4 — 矩阵乘法的六种代数形态。',
  'ch16.short': '模式图鉴',

  // Ch01
  'ch01.numbers': '数字',
  'ch01.columns': '列',
  'ch01.rows': '行',
  'ch01.rank1': '秩-1 之和',
  'ch01.numbers.cap': '矩阵作为数字网格,无几何承诺。',
  'ch01.columns.cap': '列向量 a₁、a₂ — Ax 是它们的线性组合。',
  'ch01.rows.cap': '行向量 r₁、r₂ — 与输入做点积得输出分量。',
  'ch01.rank1.cap': '秩-1 之和: A = Σ σᵢuᵢvᵢᵀ。每片都是一个外积。',
  'ch01.matrix': '矩阵 A',
  'ch01.expansion': '展开',
  'ch01.numbers.body':
    '矩阵的最朴素的看法: 一组数字。但它什么都不告诉你 —— 它对应什么变换? 输入到哪里? 切换右侧标签看其它视角。',
  'ch01.meta': '同一矩阵 · 四种几何意义',

  // Ch02
  'ch02.drag': '拖动 a 或 b 的端点。',
  'ch02.outer': '外积 abᵀ',
  'ch02.outer.note': '外积得到一个秩-1 矩阵。每一列都是 a 的倍数。',

  // Ch03
  'ch03.drag': '拖动 x 的端点 → 实时看 Ax 在哪里。',
  'ch03.matrix': 'A · x = Ax',
  'ch03.linear': '线性组合',
  'ch03.note':
    '关键洞察: Ax 永远落在 A 的列空间里。改变 x 就是改变组合系数。',
  'ch03.colspace': '列空间 C(A)',
  'ch03.row.title': '行视角: xᵀA',
  'ch03.row.note':
    '对偶解读: xᵀA 是 A 的行向量的线性组合。同一个矩阵，两种读法。',

  // Ch04
  'ch04.input': '输入空间',
  'ch04.output': '输出空间',
  'ch04.input.cap': 'C(Aᵀ) 行空间, N(A) 零空间; 二者正交。',
  'ch04.output.cap': 'C(A) 列空间, N(Aᵀ) 左零空间; 二者正交。',
  'ch04.summary': '四个子空间',
  'ch04.colspace': '列空间 — 输出能到达的所有点',
  'ch04.rowspace': '行空间 — 输入中"被矩阵看到"的方向',
  'ch04.nullspace': '零空间 — 被压成 0 的输入',
  'ch04.leftnull': '左零空间 — 不在像中的输出方向',
  'ch04.note':
    '当 A 满秩(det ≠ 0), 零空间和左零空间都只有原点。把第二行设为第一行的倍数,马上看到秩塌陷的几何。',

  // Ch05
  'ch05.dot': '点积视角',
  'ch05.columns': '列视角',
  'ch05.rows': '行视角',
  'ch05.outer': '外积之和',
  'ch05.fourways': '四种解读',
  'ch05.input': '输入 A · B',
  'ch05.expansion': '展开式',
  'ch05.dot.body': '最经典: (AB)ᵢⱼ 是 A 的第 i 行点 B 的第 j 列。',
  'ch05.columns.body':
    'AB 的第 j 列 = A 乘 B 的第 j 列。每一列都是 A 的列的线性组合。',
  'ch05.rows.body': 'AB 的第 i 行 = A 的第 i 行乘 B。每一行都是 B 的行的线性组合。',
  'ch05.outer.body':
    'AB = Σ aₖ bₖᵀ —— 把矩阵乘法拆成秩-1 的和。这是 SVD 的根。',

  // Ch06
  'ch06.perm': '置换 P',
  'ch06.diag': '对角 D',
  'ch06.elim': '消元 E',
  'ch06.rot': '旋转 R',
  'ch06.cap': '观察单位方格被怎么变形。',
  'ch06.what': '它做了什么',
  'ch06.permutation.note':
    '交换两行(或两列)。几何上是镜像。det = ±1, 总是正交。',
  'ch06.diagonal.note':
    '沿坐标轴拉伸 — x 方向 ×2, y 方向 ÷2。是最简单的非平凡变换。',
  'ch06.elimination.note':
    '把第二行减掉第一行的若干倍。几何上是剪切, 列空间不变。',
  'ch06.rotation.note':
    '旋转一个角度。det = 1, 列正交且单位长度。是 SVD 的 U 和 V 的真身。',

  // Ch07
  'ch07.colpick': '挑选独立列',
  'ch07.cap': '若两列共线 → 秩 1, 只留一根。',
  'ch07.note':
    'CR 把 A 拆成"独立列(C) × 组合系数(R)"。当列线性相关时, R 告诉你怎么用 C 凑出原列。',
  'ch07.patterns.title': 'P1 / P2 — 同一个 A，两种读法',
  'ch07.p1.label': 'A 的列 = C 的列的线性组合',
  'ch07.p2.label': 'A 的行 = R 的行的线性组合',
  'ch07.patterns.rank0': '秩 0 — A 是零矩阵，C 和 R 都塌成 0，没有独立列/行。',
  'ch07.patterns.rank1':
    '秩 1 — 挑一个独立列（P1）+ 一个独立行（P2）。R 记录相关性。',
  'ch07.patterns.rank2':
    '秩 2 — 每列都独立（P1 全留）；每行都独立（P2 全留）；R = I。',

  // Ch08
  'ch08.gaussian': '高斯消元',
  'ch08.cap': '行向量在消元下如何旋转。',
  'ch08.singular': '主元为 0, LU 不存在(需要置换)。',
  'ch08.step.input': '原矩阵',
  'ch08.step.eliminate': '消第二行',
  'ch08.step.factored': '完成',
  'ch08.s0': '从两个行向量开始。我们要把第二行变得只在 y 方向有分量。',
  'ch08.s1': '减去第一行的 ℓ 倍, 把 r₂ 的 x 分量打掉。这个 ℓ 就是 L 中的元素。',
  'ch08.s2': '完成: A = L · U 。L 记录消元乘数, U 是上三角。',
  'ch08.peeling.title': '秩-1 剥离',
  'ch08.peeling.note':
    'LU 的另一种读法: A = (L 的第 1 列)·(U 的第 1 行)ᵀ + 残差。对 2×2 残差为 0 — 一次外积就把 A 剥光。',

  // Ch09
  'ch09.cap': '逐步把列正交化, 同时记下 R。',
  'ch09.step.input': '两列向量',
  'ch09.step.q1': '归一第一列',
  'ch09.step.subtract': '减去投影',
  'ch09.step.q2': '正交基底',
  'ch09.s0': '从 A 的两列开始。它们一般不正交、不单位长度。',
  'ch09.s1': '把 a₁ 归一化,得到 q₁。R 的对角元 r₁₁ = ||a₁||。',
  'ch09.s2': '从 a₂ 中减掉它在 q₁ 上的投影, 剩下的就垂直于 q₁。',
  'ch09.s3': '把剩下的也归一化得 q₂。Q = [q₁ | q₂] 是正交矩阵。',

  // Ch10
  'ch10.cap': '拖动 x, 红色 Ax 落在主轴方向时纯拉伸 — 那就是特征向量。',
  'ch10.symA': '对称矩阵 A',
  'ch10.eigen': '特征值与特征向量',
  'ch10.symmetric': '对称 ⇒ 实特征值 + 正交特征向量',
  'ch10.note':
    '对称矩阵的特征向量正交。这意味着 A 在 q₁ 方向纯拉伸 λ₁ 倍, 在 q₂ 方向纯拉伸 λ₂ 倍 — 没有旋转。',
  'ch10.complex':
    '当前矩阵的特征值是复数 — 通常是有旋转分量。这一章只展示实对称情况。',

  // Ch11
  'ch11.three': '三步几何',
  'ch11.cap': '蓝色椭圆 = 单位圆经过当前阶段的像。',
  'ch11.steps': '步骤',
  'ch11.factors': '分解结果',
  'ch11.step.input': '单位圆',
  'ch11.step.rotate1': 'Vᵀ · 旋转',
  'ch11.step.scale': 'Σ · 缩放',
  'ch11.step.rotate2': 'U · 旋转',
  'ch11.s0':
    '从单位圆开始 — 任何矩阵都能写成对它做三步: 旋转 → 缩放 → 旋转。',
  'ch11.s1': 'Vᵀ 是个旋转, 它把输入旋到主奇异方向。',
  'ch11.s2': 'Σ 沿坐标轴拉伸: σ₁ 倍 x, σ₂ 倍 y。圆变成轴对齐的椭圆。',
  'ch11.s3': 'U 又是个旋转, 把椭圆转到 A 的输出方向。完成 A = U Σ Vᵀ。',

  'ch12.eyebrow': '五分解 · 一览',
  'ch12.input': '输入矩阵 A',
  'ch12.note':
    '五种分解从五个角度看同一个 A。CR 找独立列；LU 是消元；QR 是 Gram–Schmidt；QΛQᵀ 是对称矩阵的特征分解；UΣVᵀ 对任意矩阵都成立。改编自 Hiranabe & Strang，CC-BY。',
  'ch12.cr.title': 'A = CR',
  'ch12.cr.note': 'C：A 的独立列。R：行最简形。秩 = C 的列数。',
  'ch12.lu.title': 'A = LU',
  'ch12.lu.note': 'L：单位下三角（消元乘子）。U：上三角（消元后）。',
  'ch12.lu.fail': '主元为 0 — LU 需要行置换 (PA = LU)。',
  'ch12.qr.title': 'A = QR',
  'ch12.qr.note': 'Q：正交列（Gram–Schmidt 得到）。R：上三角（投影系数）。',
  'ch12.evd.title': 'A = QΛQᵀ',
  'ch12.evd.note': 'Q：正交特征向量。Λ：对角上的特征值。仅对称矩阵适用。',
  'ch12.evd.fail': 'A 不对称 — QΛQᵀ 不成立。试试 [[2, 1], [1, 2]]。',
  'ch12.svd.title': 'A = UΣVᵀ',
  'ch12.svd.note': 'U、V：正交基。Σ：对角上的奇异值。对任意矩阵都成立。',
  'ch12.rank': '秩',
  'ch12.symmetric.yes': '对称',
  'ch12.symmetric.no': '不对称',

  'ch13.eyebrow': '特征值住在哪里',
  'ch13.kinds': 'A 属于：',
  'ch13.kinds.empty': '无特殊分类。',
  'ch13.reference': '参考：12 种典型',
  'ch13.note':
    '改编自 Hiranabe《Map of Eigenvalues》(CC-BY)。每格展示一种典型矩阵、它的特征值必须落在的区域、以及一个代表例子。',
  'ch13.k.zero': '零 · O',
  'ch13.k.identity': '单位 · I',
  'ch13.k.projection': '投影 · P=P²=Pᵀ',
  'ch13.k.nilpotent': '幂零 · Nᵏ=O',
  'ch13.k.scalar': '标量 · zI',
  'ch13.k.symmetric': '对称 · S=Sᵀ',
  'ch13.k.posdef': '正定 · S₊',
  'ch13.k.antisym': '反对称 · A=−Aᵀ',
  'ch13.k.orthogonal': '正交 · QQᵀ=I',
  'ch13.k.markov': 'Markov · 1ᵀA=1',
  'ch13.k.invertible': '可逆 · |A|≠0',
  'ch13.k.singular': '奇异 · |A|=0',
  'ch13.k.diagonal': '对角',
  'ch13.k.possemi': '半正定',

  'ch14.eyebrow': '嵌套图谱',
  'ch14.invertible': '可逆',
  'ch14.singular': '奇异',
  'ch14.samples': '试试这些：',
  'ch14.note':
    '改编自 Hiranabe《Matrix World》(CC-BY)。每个圈代表一个矩阵家族；A 命中的所有圈都会高亮。越往内圈结构越强。',
  'ch14.s.identity': '单位 I',
  'ch14.s.zero': '零 O',
  'ch14.s.symmetric': '对称',
  'ch14.s.orthogonal': '正交（旋转 60°）',
  'ch14.s.permutation': '置换 [[0,1],[1,0]]',
  'ch14.s.projection': '投影 [[1,0],[0,0]]',
  'ch14.s.markov': 'Markov',
  'ch14.s.defective': '亏损 [[1,1],[0,1]]',
  'ch14.s.singular': '奇异 [[1,2],[2,4]]',
  'ch14.k.matrix': '矩阵 (m×n)',
  'ch14.k.diagonalizable': '可对角化',
  'ch14.k.normal': '正规 · AᵀA = AAᵀ',
  'ch14.k.symmetric': '对称',
  'ch14.k.possemi': '半正定',
  'ch14.k.posdef': '正定',
  'ch14.k.orthogonal': '正交',
  'ch14.k.permutation': '置换',
  'ch14.k.projection': '投影',
  'ch14.k.diagonal': '对角',
  'ch14.k.identity': '单位',
  'ch14.k.zero': '零',
  'ch14.k.scalar': '标量',
  'ch14.k.markov': 'Markov',
  'ch14.k.invertible': '可逆',
  'ch14.k.singular': '奇异',
  'ch14.k.antisym': '反对称',
  'ch14.k.nilpotent': '幂零',

  'ch15.eyebrow': '四子空间 · 解的几何',
  'ch15.input.A': 'A',
  'ch15.input.b': 'b',
  'ch15.input.space': '输入空间 ℝⁿ',
  'ch15.output.space': '输出空间 ℝᵐ',
  'ch15.consistent': '有解',
  'ch15.notconsistent': '无解',
  'ch15.kind.unique': '唯一解',
  'ch15.kind.line': '解的直线',
  'ch15.kind.inconsistent': '无解 · 显示最小二乘解',
  'ch15.kind.all': '解集 = 整个 ℝⁿ',
  'ch15.legend.rowspace': 'C(Aᵀ) 行空间',
  'ch15.legend.null': 'N(A) 零空间',
  'ch15.legend.solutions': 'x_p + t·n',
  'ch15.legend.colspace': 'C(A) 列空间',
  'ch15.legend.leftnull': 'N(Aᵀ) 左零空间',
  'ch15.note':
    '改编自 Strang × Hiranabe (CC-BY)。每个 Ax = b 都长这样：x_p 在行空间里，加上零空间任意向量。b 在列空间里时方程有解。',

  'ch16.eyebrow': '六种代数形态',
  'ch16.note':
    '改编自 Hiranabe / Strang《Practical Patterns》(Figures 7–10, CC-BY)。同一种乘法，六种解读 — 列组合、行组合、左右对角缩放、以及三矩阵中间的 rank-1 之和。',
  'ch16.p1.title': 'A · c — 列组合',
  'ch16.p1.note': 'A · c 把 A 的列按 c 的权重相加。Ax = b 的根本形态。',
  'ch16.p2.title': 'rᵀ · A — 行组合',
  'ch16.p2.note': 'rᵀ · A 把 A 的行按 r 的权重相加。是列组合的对偶。',
  'ch16.p1p.title': 'A · D — 列缩放',
  'ch16.p1p.note': 'A · D 把 D 的对角元乘到 A 的对应列上。',
  'ch16.p2p.title': 'D · A — 行缩放',
  'ch16.p2p.note': 'D · A 把 D 的对角元乘到 A 的对应行上。',
  'ch16.p3.title': 'X · D · c — 向量配方',
  'ch16.p3.note':
    '出现在微分方程与迭代式中：c 的每一项被对角/特征值缩放后再与 X 的对应列配对求和。',
  'ch16.p4.title': 'Y · D · Xᵀ — 秩-1 之和',
  'ch16.p4.note':
    'Y · D · Xᵀ = Σᵢ dᵢ · yᵢ · xᵢᵀ。对称 EVD（QΛQᵀ）和 SVD（UΣVᵀ）背后的同一种结构。',
};
